import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState
} from 'react';
import {useQuery} from '@tanstack/react-query';
import {initializePaddle, Paddle, Environments} from '@paddle/paddle-js';
import baseUrl from '../api/baseUrl';
import {mergeEmbeddedSettings} from '../lib/embedded-app-settings';

declare global {
    interface Window {
        __APP_SETTINGS__?: unknown;
    }
}

// Fallback path only: in production the settings are embedded in index.html
// by AppSettingsInjectionMiddleware, so this fetch (including the 403 →
// isSiteDisabled detection) only runs when the embedded block is absent —
// the Vite dev server, or a server-side injection failure.
const fetchAppSettings = async (): Promise<AppSettingsData> => {
    const response = await fetch(`${baseUrl}/api/app-settings`);

    if (response.status === 403) {
        return {
            ...emptyAppSettingsData,
            isLoading: false,
            isSiteDisabled: true
        };
    }

    if (!response.ok) {
        throw new Error('Network response was not ok');
    }

    return response.json();
};

type AppSettingsData = {
    isLoading: boolean;
    isSiteDisabled: boolean;
    fromEmailAddress: string;
    isDatabaseAvailable: boolean;
    databaseErrorMessage: string | null;
    auth: AppSettingsAuth;
    paddle: AppSettingsPaddle;
    turnstile: AppSettingsTurnstile;
};

type AppSettingsTurnstile = {
    siteKey: string;
};

export type AppSettingsPaddle = {
    /** 'none' (billing off), 'sandbox' or 'production'. */
    environment: string;
    subscriptionSignupEnabled: boolean;
    clientSideToken: string;
    platformFeeProductId: string;
    adminSeatsProductId: string;
    resourceBlocksProductId: string;
    // The price set on sale: monthly ids, and annual ids when offered.
    platformFeePriceId: string;
    adminSeatsPriceId: string;
    resourceBlocksPriceId: string;
    platformFeeAnnualPriceId: string;
    adminSeatsAnnualPriceId: string;
    resourceBlocksAnnualPriceId: string;
    // What the set on sale includes.
    includedMaintainerSeats: number;
    includedResources: number;
    seatsPerBlock: number;
    resourcesPerBlock: number;
};

/** Paddle.js only knows these two; anything else means billing is off. */
const isPaddleEnvironment = (value: string): value is Environments =>
    value === 'sandbox' || value === 'production';

type AppSettingsAuth = {
    microsoft: MicrosoftAuth;
    google: GoogleAuth;
};

type MicrosoftAuth = {
    enabled: boolean;
    clientId: string;
};

type GoogleAuth = {
    enabled: boolean;
    clientId: string;
};

const emptyAppSettingsData: AppSettingsData = {
    isLoading: true,
    isSiteDisabled: false,
    fromEmailAddress: '',
    isDatabaseAvailable: true,
    databaseErrorMessage: null,
    auth: {
        microsoft: {
            clientId: '',
            enabled: false
        },
        google: {
            clientId: '',
            enabled: false
        }
    },
    paddle: {
        environment: 'sandbox',
        subscriptionSignupEnabled: false,
        clientSideToken: '',
        platformFeeProductId: '',
        adminSeatsProductId: '',
        resourceBlocksProductId: '',
        platformFeePriceId: '',
        adminSeatsPriceId: '',
        resourceBlocksPriceId: '',
        platformFeeAnnualPriceId: '',
        adminSeatsAnnualPriceId: '',
        resourceBlocksAnnualPriceId: '',
        includedMaintainerSeats: 5,
        includedResources: 100,
        seatsPerBlock: 5,
        resourcesPerBlock: 100
    },
    turnstile: {
        siteKey: ''
    }
};

// Settings injected server-side into index.html (production). A malformed
// injected script fails to execute, leaving the global undefined — which
// lands on the fetch fallback with no extra guarding needed.
const embeddedSettings = mergeEmbeddedSettings(
    window.__APP_SETTINGS__,
    emptyAppSettingsData
);

type AppSettingsContextType = AppSettingsData & {
    paddleInstance?: Paddle;
};

const AppSettingsContext = createContext<AppSettingsContextType>(
    emptyAppSettingsData as AppSettingsContextType
);

type AppSettingsProviderProps = {
    children: ReactNode;
};

export const AppSettingsProvider: React.FC<AppSettingsProviderProps> = ({
    children
}) => {
    const {data, error} = useQuery({
        queryKey: ['appSettings'],
        queryFn: fetchAppSettings,
        staleTime: Infinity, // Prevents refetching
        // With staleTime Infinity the embedded settings are fresh forever, so
        // no fetch ever fires when they are present.
        initialData: embeddedSettings
    });

    const appSettingsData = data ?? emptyAppSettingsData;
    const [paddleInstance, setPaddleInstance] = useState<Paddle>();

    useEffect(() => {
        if (!appSettingsData.isLoading) {
            // The server sends empty string for unconfigured values (it clears
            // the appsettings.json overridden-sentinel before the payload
            // leaves the backend). With billing off there is no token and
            // no environment Paddle.js would accept, so it is never loaded.
            const {environment, clientSideToken} = appSettingsData.paddle;
            if (clientSideToken && isPaddleEnvironment(environment)) {
                initializePaddle({
                    environment,
                    token: clientSideToken
                }).then((paddle: Paddle | undefined) => {
                    if (paddle) {
                        setPaddleInstance(paddle);
                    }
                });
            } else if (environment !== 'none') {
                console.warn('Paddle client token is not configured.');
            }
        }
    }, [appSettingsData]);

    const contextValue: AppSettingsContextType = {
        ...appSettingsData,
        paddleInstance
    };

    // Never block rendering: settings and database problems surface as
    // banners while the app renders with defaults. The error banner only
    // appears once React Query's retries are exhausted; the database banner
    // is gated on !isLoading so the optimistic default (isDatabaseAvailable:
    // true) never flashes or suppresses incorrectly.
    const showSettingsUnavailableBanner = !!error;
    const showDatabaseUnavailableBanner =
        !appSettingsData.isLoading && !appSettingsData.isDatabaseAvailable;

    return (
        <AppSettingsContext.Provider value={contextValue}>
            {appSettingsData.isSiteDisabled && (
                <div
                    className="alert alert-warning text-center mb-0 rounded-0"
                    role="alert"
                >
                    Site Disabled
                </div>
            )}
            {showSettingsUnavailableBanner && (
                <div
                    className="alert alert-warning text-center mb-0 rounded-0"
                    role="alert"
                >
                    Some settings could not be loaded, so some features (such as
                    sign-in options and billing) may be unavailable. Refreshing
                    the browser may help.
                </div>
            )}
            {showDatabaseUnavailableBanner && (
                <div
                    className="alert alert-warning text-center mb-0 rounded-0"
                    role="alert"
                >
                    The database is currently unavailable, so data may not load
                    or save.
                    {appSettingsData.databaseErrorMessage && (
                        <> {appSettingsData.databaseErrorMessage}</>
                    )}
                </div>
            )}
            {children}
        </AppSettingsContext.Provider>
    );
};

const useAppSettings = () => {
    const context = useContext(AppSettingsContext);
    if (context === undefined) {
        throw new Error(
            'useAppSettings must be used within a AppSettingsProvider'
        );
    }
    return context;
};

// eslint-disable-next-line react-refresh/only-export-components
export default useAppSettings;
